import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { VRMLoaderPlugin, VRMUtils, type VRM, type VRMHumanBoneName } from '@pixiv/three-vrm';
import type { AnimatedModel } from './CharacterModel';
import { aimBone } from './CharacterModel';
import { OUTFITS, paintLegs, paintSkirt, paintSolid, paintTops, type Outfit, type OutfitName } from './outfits';

/**
 * An anime-style VRM character (VRoid) driven by humanoid animations from the
 * Quaternius Universal Animation Library (both CC0 / free-use). Clips are
 * retargeted at load time onto the VRM's normalized humanoid rig.
 */

/** Universal Animation Library (Unreal-style names) → VRM humanoid bones. */
const BONE_MAP: Record<string, VRMHumanBoneName> = {
  pelvis: 'hips',
  spine_01: 'spine',
  spine_02: 'chest',
  spine_03: 'upperChest',
  neck_01: 'neck',
  Head: 'head',
  thigh_l: 'leftUpperLeg',
  calf_l: 'leftLowerLeg',
  foot_l: 'leftFoot',
  ball_l: 'leftToes',
  thigh_r: 'rightUpperLeg',
  calf_r: 'rightLowerLeg',
  foot_r: 'rightFoot',
  ball_r: 'rightToes',
};
for (const [s, side] of [['l', 'left'], ['r', 'right']] as const) {
  BONE_MAP[`clavicle_${s}`] = `${side}Shoulder`;
  BONE_MAP[`upperarm_${s}`] = `${side}UpperArm`;
  BONE_MAP[`lowerarm_${s}`] = `${side}LowerArm`;
  BONE_MAP[`hand_${s}`] = `${side}Hand`;
  BONE_MAP[`thumb_01_${s}`] = `${side}ThumbMetacarpal`;
  BONE_MAP[`thumb_02_${s}`] = `${side}ThumbProximal`;
  BONE_MAP[`thumb_03_${s}`] = `${side}ThumbDistal`;
  for (const [src, dst] of [['index', 'Index'], ['middle', 'Middle'], ['ring', 'Ring'], ['pinky', 'Little']] as const) {
    BONE_MAP[`${src}_01_${s}`] = `${side}${dst}Proximal` as VRMHumanBoneName;
    BONE_MAP[`${src}_02_${s}`] = `${side}${dst}Intermediate` as VRMHumanBoneName;
    BONE_MAP[`${src}_03_${s}`] = `${side}${dst}Distal` as VRMHumanBoneName;
  }
}

export type VrmClips = {
  idle: string;
  walk: string;
  run: string;
  /** Game-facing one-shot names → source clip names. */
  oneShots: Record<string, string>;
};

type LocoState = 'idle' | 'walk' | 'run';

const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _qi = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
/** Her glasses, as shares of the distance between her eyes: lens size, how far above the eye line, gap, wire thickness, distance off the face. */
const GLASSES = { lensWidth: 0.92, lensHeight: 0.78, lift: 0.1, spread: 0.04, wire: 0.035, standOff: 0.12, color: '#221d1b' };
const _pa = new THREE.Vector3();
const _pb = new THREE.Vector3();
const _pc = new THREE.Vector3();
const _pd = new THREE.Vector3();
const _pn = new THREE.Vector3();
const _pf = new THREE.Vector3();

export class VrmCharacter implements AnimatedModel {
  readonly root = new THREE.Group();
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  /** Ground speed (m/s) each locomotion clip covers at timeScale 1. */
  private readonly clipSpeed = new Map<string, number>();
  private loco: LocoState | null = null;
  private oneShot: THREE.AnimationAction | null = null;
  private headYaw = 0;
  private headPitch = 0;
  /** +1 when a positive pitch tips this rig's head down (depends on which way it faces). */
  pitchSign = 1;
  private handBlend = 0;
  private handLow = 0;
  private leftBlend = 0;
  /** Normalized hips position in the rest pose (straight legs, feet on the ground). */
  private readonly hipsRest = new THREE.Vector3();
  private readonly armLen = { upper: 0.26, fore: 0.24 };
  /** The model's own clothing textures, restored for the stock uniform. */
  private readonly baseMaps = new Map<THREE.MeshStandardMaterial, THREE.Texture | null>();
  /** Textures painted for the current outfit (disposed when she changes). */
  private outfitMaps: THREE.Texture[] = [];
  private outfit: OutfitName = 'uniform';
  private readonly scarf: THREE.Group;
  private fear = 0;
  private blinkTimer = 2;
  private blinkPhase = 0;
  private surprised = 0;
  private readonly ownedMaterials: THREE.Material[] = [];
  /** Right-hand finger bones and how far each curls around the phone (radians). */
  private readonly grip: { bone: THREE.Object3D; angle: number }[] = [];
  /** Sign of the curl about the rig's Z axis that bends fingers toward the palm. */
  private curlSign = 1;
  /** Sign that turns cross(fingers, across-knuckles) into the palm's outward normal. */
  private palmSign = 1;

  private constructor(
    private readonly vrm: VRM,
    private readonly clips: VrmClips,
    retargeted: Map<string, THREE.AnimationClip>,
    speeds: Map<string, number>,
  ) {
    this.root.add(vrm.scene);
    vrm.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      // Swap the toon (MToon) shading for standard PBR so she reacts to the night's
      // lights and fog like the rest of the world; toon shading blows out under the
      // nearby point lights. Outline passes are hidden.
      const convert = (m: THREE.Material): THREE.Material => {
        const toon = m as THREE.Material & {
          isMToonMaterial?: boolean;
          isOutline?: boolean;
          map?: THREE.Texture | null;
          normalMap?: THREE.Texture | null;
          emissiveMap?: THREE.Texture | null;
          color?: THREE.Color;
          alphaTest?: number;
        };
        if (!toon.isMToonMaterial) return m;
        if (toon.isOutline) {
          m.visible = false;
          return m;
        }
        const std = new THREE.MeshStandardMaterial({
          name: m.name,
          map: toon.map ?? null,
          normalMap: toon.normalMap ?? null,
          color: toon.color ? toon.color.clone() : new THREE.Color('#ffffff'),
          transparent: m.transparent,
          alphaTest: toon.alphaTest ?? 0,
          side: m.side,
          depthWrite: m.depthWrite,
          roughness: 0.78,
          metalness: 0,
        });
        // Eyes and highlights read better slightly self-lit.
        if (/Eye(Iris|Highlight|White)/.test(m.name)) {
          std.emissive.set('#ffffff');
          std.emissiveMap = toon.map ?? null;
          std.emissiveIntensity = 0.12;
        }
        this.ownedMaterials.push(std);
        return std;
      };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(convert) : convert(mesh.material);
    });
    this.mixer = new THREE.AnimationMixer(vrm.scene);
    for (const [name, clip] of retargeted) this.actions.set(name, this.mixer.clipAction(clip));
    // Fingers point along the rig's X axis in the rest pose (palms down), so a
    // rotation about Z curls them toward the palm; which way depends on the side.
    const h = vrm.humanoid;
    const upper = h.getNormalizedBoneNode('rightUpperArm');
    const handNode = h.getNormalizedBoneNode('rightHand');
    if (upper && handNode) {
      vrm.scene.updateMatrixWorld(true);
      const a = vrm.scene.worldToLocal(upper.getWorldPosition(new THREE.Vector3()));
      const b = vrm.scene.worldToLocal(handNode.getWorldPosition(new THREE.Vector3()));
      this.curlSign = b.x > a.x ? -1 : 1;
    }
    // In the rest pose the palms face down: calibrate the palm normal's sign there.
    const n = this.palmNormal(new THREE.Vector3(), new THREE.Vector3());
    if (n) this.palmSign = vrm.scene.localToWorld(n.clone()).sub(vrm.scene.localToWorld(new THREE.Vector3())).y > 0 ? -1 : 1;
    // A light grip: fingers wrap the phone's edge rather than making a fist.
    for (const finger of ['Index', 'Middle', 'Ring', 'Little'] as const) {
      for (const [joint, angle] of [['Proximal', 0.45], ['Intermediate', 0.55], ['Distal', 0.35]] as const) {
        const bone = h.getNormalizedBoneNode(`right${finger}${joint}` as VRMHumanBoneName);
        if (bone) this.grip.push({ bone, angle });
      }
    }
    for (const [name, v] of speeds) this.clipSpeed.set(name, v);
    this.scarf = this.buildScarf();
    this.buildGlasses();
    const hips = h.getNormalizedBoneNode('hips');
    if (hips) this.hipsRest.copy(hips.position);
    const ua = h.getNormalizedBoneNode('leftUpperArm');
    const la = h.getNormalizedBoneNode('leftLowerArm');
    const lh = h.getNormalizedBoneNode('leftHand');
    if (ua && la && lh) {
      vrm.scene.updateMatrixWorld(true);
      const p = (o: THREE.Object3D) => o.getWorldPosition(new THREE.Vector3());
      this.armLen.upper = p(ua).distanceTo(p(la));
      this.armLen.fore = p(la).distanceTo(p(lh));
    }
  }

  /** The animation library, downloaded and parsed once and shared by every outfit. */
  private static readonly animCache = new Map<string, Promise<GLTF>>();

  static async load(vrmUrl: string, animUrl: string, clips: VrmClips, onProgress?: (e: ProgressEvent) => void): Promise<VrmCharacter> {
    const loader = new GLTFLoader();
    loader.register((parser) => new VRMLoaderPlugin(parser));
    let animLoad = VrmCharacter.animCache.get(animUrl);
    if (!animLoad) {
      animLoad = new GLTFLoader().loadAsync(animUrl);
      VrmCharacter.animCache.set(animUrl, animLoad);
      animLoad.catch(() => VrmCharacter.animCache.delete(animUrl));
    }
    const [gltf, anims] = await Promise.all([loader.loadAsync(vrmUrl, onProgress), animLoad]);
    const vrm = gltf.userData.vrm as VRM;
    VRMUtils.removeUnnecessaryVertices(gltf.scene);
    VRMUtils.combineSkeletons(gltf.scene);
    // The game faces -Z. Detect which way this model faces from its arms.
    const facesPlusZ = VrmCharacter.leftSideX(vrm) > 0;
    const { clips: retargeted, speeds } = retarget(anims, vrm);
    if (facesPlusZ) vrm.scene.rotation.y = Math.PI;
    const character = new VrmCharacter(vrm, clips, retargeted, speeds);
    character.pitchSign = facesPlusZ ? 1 : -1;
    character.resetPose();
    return character;
  }

  /** +X if the model's left arm is on +X (i.e. it faces +Z). */
  private static leftSideX(vrm: VRM): number {
    vrm.scene.updateMatrixWorld(true);
    const l = vrm.humanoid.getNormalizedBoneNode('leftUpperArm')?.getWorldPosition(new THREE.Vector3()).x ?? 0;
    const r = vrm.humanoid.getNormalizedBoneNode('rightUpperArm')?.getWorldPosition(new THREE.Vector3()).x ?? 0;
    return l - r;
  }

  locomotion(speed: number, walkSpeed: number, _runSpeed: number, runFrom: number): void {
    if (this.oneShot) return;
    const state: LocoState = speed < 0.12 ? 'idle' : speed < runFrom ? 'walk' : 'run';
    const clipName = this.clips[state];
    if (state !== this.loco) {
      const next = this.actions.get(clipName);
      const prev = this.loco ? this.actions.get(this.clips[this.loco]) : undefined;
      if (next) {
        next.reset().setEffectiveWeight(1).play();
        if (prev) prev.crossFadeTo(next, 0.3, false);
      }
      this.loco = state;
    }
    const action = this.actions.get(clipName);
    if (!action) return;
    if (state === 'idle') {
      action.timeScale = 1;
      return;
    }
    // Match the clip's stride to her actual ground speed so feet don't slide.
    const ref = this.clipSpeed.get(clipName) ?? walkSpeed;
    action.timeScale = THREE.MathUtils.clamp(speed / ref, 0.4, 2.2);
  }

  playOnce(name: string, fade = 0.2): void {
    const action = this.actions.get(this.clips.oneShots[name] ?? name);
    if (!action) return;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    if (this.loco) this.actions.get(this.clips[this.loco])?.crossFadeTo(action, fade, false);
    this.oneShot = action;
    if (name === 'HitRecieve') this.surprised = 1;
  }

  resetPose(): void {
    this.mixer.stopAllAction();
    this.oneShot = null;
    this.loco = null;
    this.surprised = 0;
    this.locomotion(0, 1, 1, 99);
    this.vrm.update(0);
  }

  /** 0..1 fear shows on her face. */
  setFear(t: number): void {
    this.fear = t;
  }

  update(dt: number): void {
    // Clips may not key the fingers: start each frame from rest so the grip doesn't accumulate.
    for (const g of this.grip) g.bone.quaternion.identity();
    this.mixer.update(dt);
    const h = this.vrm.humanoid;
    // The library idle is a guarded, wide stance: settle it into an ordinary one.
    const idle = this.actions.get(this.clips.idle);
    const idleWeight = idle?.isRunning() ? idle.getEffectiveWeight() : 0;
    if (idleWeight > 0.001) this.relaxStance(idleWeight);
    if (this.leftBlend > 0.001) this.raiseLeftHand(this.leftBlend);
    // Head turn (looking back / around) layered over the animation.
    if (Math.abs(this.headYaw) > 1e-3 || Math.abs(this.headPitch) > 1e-3) {
      const neck = h.getNormalizedBoneNode('neck');
      const head = h.getNormalizedBoneNode('head');
      if (neck) neck.quaternion.premultiply(_q.setFromAxisAngle(_up, this.headYaw * 0.35));
      if (head) {
        head.quaternion.premultiply(_q.setFromAxisAngle(_up, this.headYaw * 0.65));
        head.quaternion.multiply(_q2.setFromAxisAngle(_v.set(1, 0, 0), this.headPitch * this.pitchSign));
      }
    }
    // Phone / flashlight arm.
    if (this.handBlend > 0.001) {
      const upper = h.getNormalizedBoneNode('rightUpperArm');
      const lower = h.getNormalizedBoneNode('rightLowerArm');
      const hand = h.getNormalizedBoneNode('rightHand');
      if (upper && lower && hand) {
        this.root.updateMatrixWorld(true);
        const low = this.handLow;
        aimBone(upper, lower, this.root.localToWorld(_v.set(0.17, 0.96 - low * 0.08, -0.12 - low * 0.08)), this.handBlend);
        aimBone(lower, hand, this.root.localToWorld(_v.set(0.08, 1.1 - low * 0.16, -0.32 - low * 0.12)), this.handBlend);
      }
      // Turn the wrist so the palm (and the phone in it) faces her eyes.
      if (hand) this.turnPalmToFace(hand);
      // Fingers close around the phone.
      for (const g of this.grip) {
        g.bone.quaternion.multiply(_q.setFromAxisAngle(_v.set(0, 0, 1), this.curlSign * g.angle * this.handBlend));
      }
    }
    this.updateFace(dt);
    this.vrm.update(dt);
  }

  private updateFace(dt: number): void {
    const em = this.vrm.expressionManager;
    if (!em) return;
    this.blinkTimer -= dt;
    if (this.blinkTimer <= 0 && this.blinkPhase === 0) this.blinkPhase = 0.001;
    if (this.blinkPhase > 0) {
      this.blinkPhase += dt / 0.16;
      if (this.blinkPhase >= 1) {
        this.blinkPhase = 0;
        // Frightened people blink more.
        this.blinkTimer = 1.2 + Math.random() * (3.5 - this.fear * 2);
      }
    }
    const blink = this.blinkPhase > 0 ? Math.sin(this.blinkPhase * Math.PI) : 0;
    this.surprised = Math.max(0, this.surprised - dt * 0.3);
    em.setValue('blink', blink);
    em.setValue('sad', THREE.MathUtils.clamp((this.fear - 0.25) * 1.1, 0, 0.75) * (1 - this.surprised));
    em.setValue('surprised', this.surprised);
  }

  /**
   * Standing naturally: legs straight and hip-width apart, hips and back
   * upright, arms hanging loose at her sides. Blended over the idle clip, so
   * its breathing still shows through a little.
   */
  private relaxStance(w: number): void {
    const h = this.vrm.humanoid;
    const ease = (name: VRMHumanBoneName, amount: number) => {
      const bone = h.getNormalizedBoneNode(name);
      if (bone) bone.quaternion.slerp(_qi, amount * w);
    };
    const hips = h.getNormalizedBoneNode('hips');
    if (hips) hips.position.lerp(this.hipsRest, w);
    ease('hips', 0.85);
    ease('spine', 0.6);
    ease('chest', 0.6);
    ease('upperChest', 0.5);
    for (const side of ['left', 'right'] as const) {
      ease(`${side}UpperLeg`, 1);
      ease(`${side}LowerLeg`, 1);
      ease(`${side}Foot`, 1);
      ease(`${side}Toes`, 1);
      ease(`${side}Shoulder`, 1);
    }
    // Arms: straight down, elbows a touch bent so the hands rest by her thighs.
    this.root.updateMatrixWorld(true);
    for (const [side, out] of [['left', -1], ['right', 1]] as const) {
      const upper = h.getNormalizedBoneNode(`${side}UpperArm`);
      const lower = h.getNormalizedBoneNode(`${side}LowerArm`);
      const hand = h.getNormalizedBoneNode(`${side}Hand`);
      if (!upper || !lower || !hand) continue;
      const s = this.root.worldToLocal(upper.getWorldPosition(_v));
      const { upper: ul, fore: fl } = this.armLen;
      const elbow = _v2.set(s.x + out * 0.035, s.y - ul * 0.99, s.z + 0.015);
      aimBone(upper, lower, this.root.localToWorld(elbow.clone()), w);
      const wrist = _v2.set(elbow.x - out * 0.01, elbow.y - fl * 0.97, elbow.z - fl * 0.22);
      aimBone(lower, hand, this.root.localToWorld(wrist), w);
      ease(`${side}Hand`, 1);
    }
  }

  /** Left hand held in front of her chest, gripping an umbrella handle. */
  private raiseLeftHand(w: number): void {
    const h = this.vrm.humanoid;
    const upper = h.getNormalizedBoneNode('leftUpperArm');
    const lower = h.getNormalizedBoneNode('leftLowerArm');
    const hand = h.getNormalizedBoneNode('leftHand');
    if (!upper || !lower || !hand) return;
    this.root.updateMatrixWorld(true);
    const s = this.root.worldToLocal(upper.getWorldPosition(_v));
    const { upper: ul } = this.armLen;
    const elbow = _v2.set(s.x - 0.02, s.y - ul * 0.93, s.z - ul * 0.32);
    aimBone(upper, lower, this.root.localToWorld(elbow.clone()), w);
    aimBone(lower, hand, this.root.localToWorld(_v2.set(s.x * 0.25, s.y - 0.13, s.z - 0.27)), w);
    hand.quaternion.slerp(_qi, w);
    // Fingers close around the handle.
    for (const finger of ['Index', 'Middle', 'Ring', 'Little'] as const) {
      for (const [joint, angle] of [['Proximal', 1.0], ['Intermediate', 1.1], ['Distal', 0.6]] as const) {
        const bone = h.getNormalizedBoneNode(`left${finger}${joint}` as VRMHumanBoneName);
        if (bone) bone.quaternion.multiply(_q.setFromAxisAngle(_v.set(0, 0, 1), -this.curlSign * angle * w));
      }
    }
  }

  /** A knitted scarf: a ring round her neck and one end hanging down the front. Built in the rest pose. */
  private buildScarf(): THREE.Group {
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: '#a3262c', roughness: 0.95 });
    this.ownedMaterials.push(mat);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.074, 0.034, 8, 18), mat);
    ring.rotation.x = Math.PI / 2;
    ring.scale.set(1, 1.12, 1.25);
    const knot = new THREE.Mesh(new THREE.SphereGeometry(0.034, 10, 8), mat);
    knot.position.set(-0.035, -0.02, -0.085);
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.26, 0.022), mat);
    tail.position.set(-0.045, -0.15, -0.095);
    tail.rotation.set(-0.12, 0, 0.1);
    for (const m of [ring, knot, tail]) {
      m.castShadow = true;
      g.add(m);
    }
    g.visible = false;
    const neck = this.vrm.humanoid.getRawBoneNode('neck');
    if (neck) {
      this.root.updateMatrixWorld(true);
      // Model space faces -Z here; sit the ring low on the neck, over the collar.
      neck.getWorldPosition(g.position);
      g.position.y -= 0.025;
      neck.attach(g);
    } else {
      this.root.add(g);
    }
    return g;
  }

  /**
   * Thin dark glasses: two soft-cornered rims, a bridge and arms back to the
   * ears, sized from her eyes and face (the eye bones and the skin round them).
   * Built in the rest pose and carried by the head bone.
   */
  private buildGlasses(): void {
    const h = this.vrm.humanoid;
    const head = h.getRawBoneNode('head');
    const eyeL = h.getRawBoneNode('leftEye');
    const eyeR = h.getRawBoneNode('rightEye');
    if (!head || !eyeL || !eyeR) return;
    this.root.updateMatrixWorld(true);
    const toRoot = new THREE.Matrix4().copy(this.root.matrixWorld).invert();
    const a = eyeL.getWorldPosition(new THREE.Vector3()).applyMatrix4(toRoot);
    const b = eyeR.getWorldPosition(new THREE.Vector3()).applyMatrix4(toRoot);
    const eyeY = (a.y + b.y) / 2;
    const spacing = Math.abs(a.x - b.x);
    const midX = (a.x + b.x) / 2;
    // The face's surface at eye height: the front of the eyes, and the width at the temples.
    let front = Infinity;
    let halfWidth = 0;
    const v = new THREE.Vector3();
    this.vrm.scene.traverse((o) => {
      const mesh = o as THREE.SkinnedMesh;
      if (!mesh.isSkinnedMesh) return;
      const names = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).map((m) => m.name).join(' ');
      if (!/face|eye|skin|body/i.test(names) || /hair/i.test(names)) return;
      mesh.skeleton.update();
      const pos = mesh.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        mesh.getVertexPosition(i, v);
        v.applyMatrix4(mesh.matrixWorld).applyMatrix4(toRoot);
        if (Math.abs(v.y - eyeY) > spacing * 0.25) continue;
        const dx = Math.abs(v.x - midX);
        if (dx < spacing * 0.75 && v.z < 0) front = Math.min(front, v.z);
        // Sides of the head (behind the eyes), for the arms.
        if (v.z > Math.min(a.z, b.z) - 0.01) halfWidth = Math.max(halfWidth, dx);
      }
    });
    if (!Number.isFinite(front)) front = Math.min(a.z, b.z) - spacing * 0.35;
    // Hair and ears can push the measured width out; the arms hug the skull under the hair.
    halfWidth = THREE.MathUtils.clamp(halfWidth || spacing, spacing * 0.85, spacing * 1.1);
    const z = front - spacing * GLASSES.standOff;
    const w = spacing * GLASSES.lensWidth;
    const hgt = w * GLASSES.lensHeight;
    const r = spacing * GLASSES.wire;
    const frameMat = new THREE.MeshStandardMaterial({ color: GLASSES.color, roughness: 0.35, metalness: 0.3 });
    const lensMat = new THREE.MeshStandardMaterial({
      color: '#c9d4dc',
      roughness: 0.08,
      metalness: 0.1,
      transparent: true,
      opacity: 0.14,
      depthWrite: false,
    });
    this.ownedMaterials.push(frameMat, lensMat);
    const g = new THREE.Group();
    g.name = 'glasses';
    // A soft-cornered rectangle (superellipse), its sides swept back a little round the face.
    const rimPoint = (cx: number, side: number, t: number) => {
      const c = Math.cos(t);
      const sn = Math.sin(t);
      const x = cx + (w / 2) * Math.sign(c) * Math.abs(c) ** 0.55;
      const y = eyeY + (hgt / 2) * Math.sign(sn) * Math.abs(sn) ** 0.55 + hgt * GLASSES.lift;
      const out = Math.max(0, (x - midX) * side - w * 0.35) / w;
      return new THREE.Vector3(x, y, z + out * w * 0.35);
    };
    for (const side of [1, -1]) {
      const cx = midX + side * (spacing / 2 + spacing * GLASSES.spread);
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i < 28; i++) pts.push(rimPoint(cx, side, (i / 28) * Math.PI * 2));
      const rim = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, true), 56, r, 5, true), frameMat);
      const lensShape = new THREE.Shape(pts.map((q) => new THREE.Vector2(q.x - cx, q.y - eyeY)));
      const lens = new THREE.Mesh(new THREE.ShapeGeometry(lensShape), lensMat);
      lens.position.set(cx, eyeY, z + r * 0.5);
      lens.renderOrder = 2;
      // Arm: from the rim's outer corner straight back past the temple to the ear, dipping at the end.
      const hinge = rimPoint(cx, side, side > 0 ? 0.35 : Math.PI - 0.35);
      const arm = new THREE.CatmullRomCurve3([
        hinge,
        new THREE.Vector3(midX + side * (halfWidth + r * 2), hinge.y + hgt * 0.05, hinge.z + spacing * 0.5),
        new THREE.Vector3(midX + side * (halfWidth + r * 2), hinge.y - hgt * 0.05, hinge.z + spacing * 1.7),
        new THREE.Vector3(midX + side * (halfWidth - r), hinge.y - hgt * 0.6, hinge.z + spacing * 2.05),
      ]);
      const armMesh = new THREE.Mesh(new THREE.TubeGeometry(arm, 16, r * 0.85, 4, false), frameMat);
      g.add(rim, lens, armMesh);
    }
    // Bridge over the nose between the two inner rims.
    const inner = (side: number) => rimPoint(midX + side * (spacing / 2 + spacing * GLASSES.spread), side, side > 0 ? Math.PI - 0.5 : 0.5);
    const l = inner(1);
    const rr = inner(-1);
    const bridge = new THREE.CatmullRomCurve3([l, new THREE.Vector3(midX, (l.y + rr.y) / 2 + hgt * 0.06, z - spacing * 0.04), rr]);
    g.add(new THREE.Mesh(new THREE.TubeGeometry(bridge, 10, r, 5, false), frameMat));
    for (const m of g.children) {
      m.castShadow = (m as THREE.Mesh).material === frameMat;
      m.frustumCulled = false;
    }
    // Into world space, then onto the head so it follows every turn and nod.
    g.applyMatrix4(this.root.matrixWorld);
    head.attach(g);
  }

  /** Change clothes: recolour the uniform's textures (kept only for the current outfit). */
  setOutfit(name: OutfitName): void {
    if (name === this.outfit) return;
    this.outfit = name;
    for (const t of this.outfitMaps) t.dispose();
    this.outfitMaps = [];
    const o = OUTFITS[name] as Outfit | null;
    // The recolours paint regions of the sample model's (Sendagaya Shino's) uniform
    // textures; on any other model they would smear the wrong areas, so she keeps
    // her own clothes, as made in VRoid (and no add-on scarf).
    const recolourable = this.ownedMaterials.some((m) => m.name.startsWith('F00_001_01_Tops'));
    for (const m of recolourable ? this.ownedMaterials : []) {
      const std = m as THREE.MeshStandardMaterial;
      const part = /Tops/.test(std.name)
        ? 'tops'
        : /Bottoms/.test(std.name)
          ? 'skirt'
          : /Shoes/.test(std.name)
            ? 'shoes'
            : /Accessory/.test(std.name)
              ? 'ribbon'
              : /Body_00_SKIN/.test(std.name)
                ? 'legs'
                : null;
      if (!part) continue;
      if (!this.baseMaps.has(std)) this.baseMaps.set(std, std.map);
      const base = this.baseMaps.get(std);
      std.visible = !(part === 'ribbon' && o?.ribbon === null);
      if (!o || !base?.image) {
        std.map = base ?? null;
        continue;
      }
      const img = base.image as CanvasImageSource & { width: number; height: number };
      const canvas =
        part === 'tops'
          ? paintTops(img, o)
          : part === 'skirt'
            ? paintSkirt(img, o)
            : part === 'shoes'
              ? paintSolid(img, o.shoes)
              : part === 'ribbon'
                ? o.ribbon
                  ? paintSolid(img, o.ribbon)
                  : null
                : paintLegs(img, o);
      if (!canvas) continue;
      const tex = base.clone();
      tex.source = new THREE.Source(canvas);
      tex.needsUpdate = true;
      this.outfitMaps.push(tex);
      std.map = tex;
    }
    this.scarf.visible = recolourable && !!o?.scarf;
    if (o?.scarf) ((this.scarf.children[0] as THREE.Mesh).material as THREE.MeshStandardMaterial).color.set(o.scarf);
  }

  holdLeft(blend: number): void {
    this.leftBlend = blend;
  }

  leftPalmWorld(out: THREE.Vector3): THREE.Vector3 {
    const h = this.vrm.humanoid;
    const hand = h.getRawBoneNode('leftHand');
    const knuckle = h.getRawBoneNode('leftMiddleProximal');
    if (!hand) return this.root.localToWorld(out.set(-0.1, 1.1, -0.3));
    hand.getWorldPosition(out);
    return knuckle ? out.lerp(knuckle.getWorldPosition(_v2), 0.5) : out;
  }

  turnHead(yaw: number, pitch = 0): void {
    this.headYaw = yaw;
    this.headPitch = pitch;
  }

  raiseRightHand(blend: number, low = 0): void {
    this.handBlend = blend;
    this.handLow = low;
  }

  handWorld(out: THREE.Vector3): THREE.Vector3 {
    const hand = this.vrm.humanoid.getRawBoneNode('rightHand');
    if (!hand) return this.root.localToWorld(out.set(0.2, 1, -0.2));
    return hand.getWorldPosition(out);
  }

  /**
   * World-space outward palm normal (unsigned until calibrated) and the finger
   * direction, from the normalized hand and knuckle bones. Null if the rig lacks them.
   */
  private palmNormal(out: THREE.Vector3, fingers: THREE.Vector3): THREE.Vector3 | null {
    const h = this.vrm.humanoid;
    const hand = h.getNormalizedBoneNode('rightHand');
    const mid = h.getNormalizedBoneNode('rightMiddleProximal');
    const index = h.getNormalizedBoneNode('rightIndexProximal');
    const little = h.getNormalizedBoneNode('rightLittleProximal');
    if (!hand || !mid || !index || !little) return null;
    const p = hand.getWorldPosition(_pa);
    fingers.copy(mid.getWorldPosition(_pb)).sub(p).normalize();
    const across = index.getWorldPosition(_pc).sub(little.getWorldPosition(_pb)).normalize();
    return out.crossVectors(fingers, across).normalize().multiplyScalar(this.palmSign);
  }

  /** Twist the hand about the finger axis so the palm faces her head. */
  private turnPalmToFace(hand: THREE.Object3D): void {
    this.root.updateMatrixWorld(true);
    const n = this.palmNormal(_pn, _pf);
    const head = this.vrm.humanoid.getNormalizedBoneNode('head');
    if (!n || !head) return;
    const f = _pf;
    const want = head.getWorldPosition(_pd).sub(hand.getWorldPosition(_pa));
    want.addScaledVector(f, -want.dot(f)).normalize();
    n.addScaledVector(f, -n.dot(f)).normalize();
    _q.setFromUnitVectors(n, want);
    const boneWorld = hand.getWorldQuaternion(new THREE.Quaternion());
    const parentWorld = hand.parent ? hand.parent.getWorldQuaternion(new THREE.Quaternion()) : new THREE.Quaternion();
    const turned = parentWorld.invert().multiply(_q).multiply(boneWorld);
    hand.quaternion.slerp(turned, this.handBlend * (1 - this.handLow));
    hand.updateMatrixWorld(true);
  }

  palmWorld(out: THREE.Vector3): THREE.Vector3 {
    const h = this.vrm.humanoid;
    const hand = h.getRawBoneNode('rightHand');
    const knuckle = h.getRawBoneNode('rightMiddleProximal');
    if (!hand || !knuckle) return this.handWorld(out);
    hand.getWorldPosition(out);
    return out.lerp(knuckle.getWorldPosition(_v2), 0.55);
  }

  forearmDir(out: THREE.Vector3): THREE.Vector3 {
    const h = this.vrm.humanoid;
    const lower = h.getRawBoneNode('rightLowerArm');
    const hand = h.getRawBoneNode('rightHand');
    if (!lower || !hand) return out.set(0, 1, 0);
    return hand.getWorldPosition(out).sub(lower.getWorldPosition(_v2)).normalize();
  }

  headWorld(out: THREE.Vector3): THREE.Vector3 {
    const head = this.vrm.humanoid.getRawBoneNode('head');
    if (!head) return this.root.localToWorld(out.set(0, 1.45, 0));
    return head.getWorldPosition(out).add(_v2.set(0, 0.06, 0));
  }

  dispose(): void {
    this.mixer.stopAllAction();
    for (const m of this.ownedMaterials) m.dispose();
    VRMUtils.deepDispose(this.vrm.scene);
  }
}

/**
 * Retarget the library clips onto the VRM's normalized rig. Each source key's
 * rotation is expressed as a world-space delta from the source T-pose
 * (A_TPose), which is exactly what a normalized VRM bone expects.
 */
function retarget(src: GLTF, vrm: VRM): { clips: Map<string, THREE.AnimationClip>; speeds: Map<string, number> } {
  const scene = src.scene;
  const tpose = src.animations.find((a) => a.name === 'A_TPose');
  const poseMixer = new THREE.AnimationMixer(scene);
  if (tpose) {
    poseMixer.clipAction(tpose).play();
    poseMixer.update(0);
  }
  scene.updateMatrixWorld(true);

  const restInverse = new Map<string, THREE.Quaternion>();
  const parentRest = new Map<string, THREE.Quaternion>();
  scene.traverse((node) => {
    if (!BONE_MAP[node.name]) return;
    restInverse.set(node.name, node.getWorldQuaternion(new THREE.Quaternion()).invert());
    parentRest.set(node.name, (node.parent ?? node).getWorldQuaternion(new THREE.Quaternion()));
  });

  // Does the source face the same way as the VRM? (Compare which side the left arm is on.)
  const srcLeft = (scene.getObjectByName('upperarm_l')?.getWorldPosition(new THREE.Vector3()).x ?? 0)
    - (scene.getObjectByName('upperarm_r')?.getWorldPosition(new THREE.Vector3()).x ?? 0);
  vrm.scene.updateMatrixWorld(true);
  const vrmLeft = (vrm.humanoid.getNormalizedBoneNode('leftUpperArm')?.getWorldPosition(new THREE.Vector3()).x ?? 0)
    - (vrm.humanoid.getNormalizedBoneNode('rightUpperArm')?.getWorldPosition(new THREE.Vector3()).x ?? 0);
  const flip = Math.sign(srcLeft) !== Math.sign(vrmLeft);

  const pelvis = scene.getObjectByName('pelvis');
  const srcHipsY = pelvis ? pelvis.getWorldPosition(new THREE.Vector3()).y : 1;
  const vrmHips = vrm.humanoid.getNormalizedBoneNode('hips');
  const vrmHipsY = vrmHips ? vrmHips.getWorldPosition(new THREE.Vector3()).y : 1;
  const scale = vrmHipsY / srcHipsY;
  const pelvisParentMatrix = pelvis?.parent ? pelvis.parent.matrixWorld.clone() : new THREE.Matrix4();
  const foot = scene.getObjectByName('foot_l');

  const clips = new Map<string, THREE.AnimationClip>();
  const speeds = new Map<string, number>();
  for (const clip of src.animations) {
    if (clip.name === 'A_TPose') continue;
    const tracks: THREE.KeyframeTrack[] = [];
    for (const track of clip.tracks) {
      const dot = track.name.lastIndexOf('.');
      const nodeName = track.name.slice(0, dot);
      const prop = track.name.slice(dot + 1);
      const boneName = BONE_MAP[nodeName];
      const target = boneName ? vrm.humanoid.getNormalizedBoneNode(boneName) : null;
      if (!target) continue;
      if (prop === 'quaternion') {
        const values = Float32Array.from(track.values);
        const pr = parentRest.get(nodeName)!;
        const ri = restInverse.get(nodeName)!;
        for (let i = 0; i < values.length; i += 4) {
          _q.fromArray(values, i).premultiply(pr).multiply(ri);
          _q.toArray(values, i);
          if (flip) {
            values[i] *= -1;
            values[i + 2] *= -1;
          }
        }
        tracks.push(new THREE.QuaternionKeyframeTrack(`${target.name}.quaternion`, Array.from(track.times), Array.from(values)));
      } else if (prop === 'position' && nodeName === 'pelvis') {
        const values = Float32Array.from(track.values);
        for (let i = 0; i < values.length; i += 3) {
          _v.fromArray(values, i).applyMatrix4(pelvisParentMatrix).multiplyScalar(scale);
          values[i] = flip ? -_v.x : _v.x;
          values[i + 1] = _v.y;
          values[i + 2] = flip ? -_v.z : _v.z;
        }
        tracks.push(new THREE.VectorKeyframeTrack(`${target.name}.position`, Array.from(track.times), Array.from(values)));
      }
    }
    clips.set(clip.name, new THREE.AnimationClip(clip.name, clip.duration, tracks));

    // Stride speed: how far the foot sweeps back relative to the hips, per half cycle.
    if (/Walk|Jog|Sprint|Run/.test(clip.name) && foot && pelvis) {
      poseMixer.stopAllAction();
      const action = poseMixer.clipAction(clip);
      action.play();
      let min = Infinity;
      let max = -Infinity;
      const steps = 40;
      for (let s = 0; s <= steps; s += 1) {
        action.time = (clip.duration * s) / steps;
        poseMixer.update(0);
        scene.updateMatrixWorld(true);
        const f = foot.getWorldPosition(_v).z - pelvis.getWorldPosition(_v2).z;
        min = Math.min(min, f);
        max = Math.max(max, f);
      }
      const stride = (max - min) * scale;
      speeds.set(clip.name, Math.max(0.3, stride / (clip.duration / 2)));
      action.stop();
    }
  }
  poseMixer.stopAllAction();
  return { clips, speeds };
}
